
import { Component, Inject, Vue, Ref, Watch } from "vue-property-decorator";
// @ts-ignore
import FullCalendar from "@fullcalendar/vue";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import { Container } from "inversify";
import { ConferenceListStore } from "../stores/conference/ConferenceListStore";
import { ConferenceFilterStore } from "../stores/conference/ConferenceFilterStore";
import { handleError } from "../../../common/src/utility/errorUtils";
import { DatePointApi, EventInput } from "@fullcalendar/core";
import CreateEventModal from "../molecules/conference/CreateEventModal.vue";
import { ConferenceCreateStore } from "../stores/conference/ConferenceCreateStore";
import { NotificationStore } from "../../../common/src/stores/NotificationStore";
import { focusOnFirstInvalidInput } from "../../../common/src/form/utils";
import LoadingOverlay from "../../../common/src/components/LoadingOverlay.vue";
import { ConferenceEditStore } from "../stores/conference/ConferenceEditStore";
import EventMap from "../molecules/conference/EventMap.vue";
import EditEventModal from "../molecules/conference/EditEventModal.vue";
import ViewEventModal from "../molecules/conference/ViewEventModal.vue";
import { ConferenceDestroyStore } from "../stores/conference/ConferenceDestroyStore";
import ConferenceTable from "../molecules/conference/ConferenceTable.vue";
import { TabStore } from "../../../common/src/stores/general/TabStore";
import TabNavItem from "../../../common/src/components/TabNavItem.vue";
import TabPane from "../../../common/src/components/TabPane.vue";
import { isAdmin, EventType, hasActiveSubscription } from "@gsx/common";
import { AuthStore } from "../../../common/src/stores/AuthStore";
import { ConferenceListItem } from "@gsx/common/src/types/http/dashboard/conference";
import { event } from "vue-gtag";
import { debounce } from "lodash";

enum Tab {
    Calendar = "calendar",
    Map = "map",
}

@Component({
    components: {
        ViewEventModal,
        ConferenceTable,
        EditEventModal,
        LoadingOverlay,
        CreateEventModal,
        FullCalendar,
        EventMap,
        TabPane,
        TabNavItem,
    },
})
export default class EventListView extends Vue {
    @Inject("container") readonly container!: Container;
    listStore: ConferenceListStore = this.container.get(ConferenceListStore);
    createStore: ConferenceCreateStore = this.container.get(ConferenceCreateStore);
    editStore: ConferenceEditStore = this.container.get(ConferenceEditStore);
    destroyStore: ConferenceDestroyStore = this.container.get(ConferenceDestroyStore);
    notificationStore: NotificationStore = this.container.get(NotificationStore);
    readonly tabStore: TabStore<Tab> = new TabStore(Tab.Calendar);
    isCreateModalVisible = false;
    isEditModalVisible = false;
    isViewModalVisible = false;
    @Ref("fullcalendar") readonly calendar!: HTMLDivElement;
    currentFullCalendarMonth = 1;
    currentFullCalendarDate = new Date();
    authStore: AuthStore = this.container.get(AuthStore);

    readonly conferenceFilterStore: ConferenceFilterStore =
        this.container.get(ConferenceFilterStore);

    created() {
        this.conferenceFilterStore.resetFilters();

        this.listStore
            .load()
            .then(() => {
                const conferenceidRouteParam = this.$route.params.conference;
                if (conferenceidRouteParam) {
                    const conference = (this.listStore.items || []).find(
                        (conference) => conference.conferenceid === conferenceidRouteParam,
                    );

                    if (conference) {
                        this.openEventModal(conference);
                    }
                }
            })
            .catch(handleError);
    }

    get calendarPlugins() {
        return [dayGridPlugin, timeGridPlugin, interactionPlugin];
    }

    datesChanged(info: any) {
        this.currentFullCalendarDate = new Date(info.view.currentStart);
        this.currentFullCalendarMonth = this.currentFullCalendarDate.getMonth();

        this.listStore.afterDate = new Date(
            this.currentFullCalendarDate.getFullYear(),
            this.currentFullCalendarMonth,
            1,
        );
        this.listStore.beforeDate = new Date(
            this.currentFullCalendarDate.getFullYear(),
            this.currentFullCalendarMonth + 1,
            0,
        );
        this.listStore.reload();
    }

    get monthForCalendar() {
        return this.currentFullCalendarDate;
    }

    get calendarLegend() {
        return [
            {
                color: "#19799f",
                label: "Conference",
            },
            {
                color: "#28a745",
                label: "Webinar/Virtual/Conference",
            },
            {
                color: "#e69334",
                label: "Corporate Milestone",
            },
            {
                color: "purple",
                label: "Social event",
            },
        ];
    }

    get calendarEvents(): EventInput[] {
        const groups = [
            {
                types: [EventType.Webinar, EventType.Virtual, EventType.Conferences],
                color: "#28a745",
            },
            {
                types: ["Product Roll Out", EventType.CorporateMilestone], // 'Product Roll Out' legacy.
                color: "#e69334",
            },
            {
                types: [EventType.SocialEvent],
                color: "purple",
            },
            {
                types: [],
                color: "#19799f",
            },
        ];

        return (this.listStore.items || []).map((conference) => ({
            id: conference.conferenceid,
            title: conference.name,
            start: conference.startDate,
            end: conference.endDate,
            color:
                groups.find((group: any) => {
                    return (
                        group.types.every((groupType: any) =>
                            conference.types?.includes(groupType),
                        ) || group.types.length === 0
                    );
                })?.color || "black",
        }));
    }
    //, EventType.Virtual, EventType.Conferences
    get canManageEvent(): boolean {
        return (
            isAdmin(this.authStore.user) ||
            this.authStore.user!.userid === this.editStore.conference!.userId
        );
    }

    displayCreateModal() {
        if (this.hasEventCreationPermission) {
            this.isCreateModalVisible = true;
        }
    }

    displayEditModal() {
        this.isEditModalVisible = true;
    }

    displayViewModal() {
        this.isViewModalVisible = true;
    }

    hideCreateModal() {
        this.isCreateModalVisible = false;
        this.createStore.clear();
    }

    hideEditModal() {
        this.isEditModalVisible = false;
    }

    hideViewModal() {
        this.isViewModalVisible = false;
    }

    handleDateClick(event: DatePointApi) {
        if (this.hasEventCreationPermission) {
            this.createStore.data.startDate = event.date;
            this.displayCreateModal();
        }
    }

    get hasEventCreationPermission() {
        return hasActiveSubscription(this.authStore.user) || isAdmin(this.authStore.user);
    }

    getConferenceById(conferenceId: string) {
        const conference = (this.listStore.items || []).find(
            (conference) => conference.conferenceid === conferenceId,
        );

        if (!conference) {
            console.warn("Could not find conference referenced by clicked event.");
            return;
        }

        return conference;
    }

    handleEventClick(conference: ConferenceListItem | undefined) {
        if (conference) {
            this.openEventModal(conference);
        }
    }

    openEventModal(conference: ConferenceListItem) {
        this.editStore.setConference(conference);

        event("Conference", {
            eventCategory: "Conference",
            eventAction: "view",
            eventLabel: conference.name,
        });

        if (this.canManageEvent) {
            this.displayEditModal();
        } else {
            this.displayViewModal();
        }
    }

    async createEvent() {
        await this.createStore.submit();

        if (this.createStore.failed) {
            focusOnFirstInvalidInput();
        } else {
            this.hideCreateModal();
            await this.listStore.reload();
        }
    }

    async editEvent() {
        await this.editStore.submit();

        if (this.editStore.failed) {
            focusOnFirstInvalidInput();
        } else {
            this.hideEditModal();
            await this.listStore.reload();
        }
    }

    async destroyEvent() {
        await this.destroyStore.delete(this.editStore.conference!.conferenceid);

        if (!this.destroyStore.failed) {
            this.hideEditModal();
            await this.listStore.reload();
        }
    }

    @Watch("listStore.query")
    @Watch("conferenceFilterStore.topics")
    @Watch("conferenceFilterStore.types")
    @Watch("conferenceFilterStore.countries")
    @Watch("conferenceFilterStore.cities")
    @Watch("conferenceFilterStore.logicalOperator")
    reloadDebounce = debounce(this.reload, 300);

    reload() {
        this.listStore.reload().catch(handleError);
    }

    get Tab() {
        return Tab;
    }
}
